import { axiosClient } from '@/libs/axios-client';

const API_BASE = '/smart-parser';

/**
 * Get all parser configurations
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Configurations with pagination
 */
export const getConfigs = async (params = {}) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/configs`, { params });
    return response.data;
  } catch (error) {
    console.error('Failed to get parser configs:', error);
    throw error;
  }
};

/**
 * Get parser configuration by ID
 * @param {string} configId - Configuration ID
 * @returns {Promise<Object>} Configuration
 */
export const getConfigById = async (configId) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/configs/${configId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get parser config:', error);
    throw error;
  }
};

/**
 * Get active configuration for company
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Active configuration
 */
export const getActiveConfigForCompany = async (companyId) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/configs/company/${companyId}/active`);
    return response.data;
  } catch (error) {
    console.error('Failed to get active config for company:', error);
    throw error;
  }
};

/**
 * Create parser configuration
 * @param {Object} configData - Configuration data
 * @returns {Promise<Object>} Created configuration
 */
export const createConfig = async (configData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/configs`, configData);
    return response.data;
  } catch (error) {
    console.error('Failed to create parser config:', error);
    throw error;
  }
};

/**
 * Update parser configuration
 * @param {string} configId - Configuration ID
 * @param {Object} configData - Updated configuration data
 * @returns {Promise<Object>} Updated configuration
 */
export const updateConfig = async (configId, configData) => {
  try {
    const response = await axiosClient.put(`${API_BASE}/configs/${configId}`, configData);
    return response.data;
  } catch (error) {
    console.error('Failed to update parser config:', error);
    throw error;
  }
};

/**
 * Delete parser configuration
 * @param {string} configId - Configuration ID
 * @returns {Promise<Object>} Success response
 */
export const deleteConfig = async (configId) => {
  try {
    const response = await axiosClient.delete(`${API_BASE}/configs/${configId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete parser config:', error);
    throw error;
  }
};

/**
 * Activate parser configuration
 * @param {string} configId - Configuration ID
 * @returns {Promise<Object>} Activated configuration
 */
export const activateConfig = async (configId) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/configs/${configId}/activate`);
    return response.data;
  } catch (error) {
    console.error('Failed to activate parser config:', error);
    throw error;
  }
};

/**
 * Clone parser configuration
 * @param {string} configId - Configuration ID to clone
 * @param {string} newName - Name for cloned configuration
 * @returns {Promise<Object>} Cloned configuration
 */
export const cloneConfig = async (configId, newName) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/configs/${configId}/clone`, { name: newName });
    return response.data;
  } catch (error) {
    console.error('Failed to clone parser config:', error);
    throw error;
  }
};

/**
 * Analyze Excel format with AI
 * @param {FormData} formData - Form data with Excel file
 * @returns {Promise<Object>} Analysis result
 */
export const analyzeFormat = async (formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/analyze`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to analyze Excel format:', error);
    throw error;
  }
};

/**
 * Test parser configuration with Excel file
 * @param {string} configId - Configuration ID
 * @param {FormData} formData - Form data with Excel file
 * @returns {Promise<Object>} Test results
 */
export const testConfig = async (configId, formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/configs/${configId}/test`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to test parser config:', error);
    throw error;
  }
};

/**
 * Parse Excel file with configuration
 * @param {FormData} formData - Form data with Excel file and configId
 * @returns {Promise<Object>} Parsed employees
 */
export const parseExcel = async (formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/parse`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to parse Excel:', error);
    throw error;
  }
};

// ==================== EML FLOW API ====================

/**
 * Preview EML attachments (without parsing)
 * @param {FormData} formData - Form data with EML file
 * @returns {Promise<Object>} EML preview data
 */
export const previewEML = async (formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/preview-eml`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to preview EML:', error);
    throw error;
  }
};

/**
 * Analyze EML and generate configuration (Learn Mode)
 * @param {FormData} formData - Form data with EML file, configName, companyId
 * @returns {Promise<Object>} Analysis result with suggested config
 */
export const analyzeEML = async (formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/analyze-eml`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to analyze EML:', error);
    throw error;
  }
};

/**
 * Process EML with configuration (Parse Mode - Production)
 * @param {FormData} formData - Form data with EML file, AB Soft file, and configId
 * @returns {Promise<Object>} Processed employees with match results
 */
export const processEML = async (formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/process-eml`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to process EML:', error);
    throw error;
  }
};

/**
 * Test configuration with EML file
 * @param {string} configId - Configuration ID
 * @param {FormData} formData - Form data with EML file and optional AB Soft file
 * @returns {Promise<Object>} Test results
 */
export const testConfigWithEML = async (configId, formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/configs/${configId}/test-eml`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to test config with EML:', error);
    throw error;
  }
};

/**
 * Get configuration version history
 * @param {string} configId - Configuration ID
 * @returns {Promise<Object>} Version history
 */
export const getConfigVersions = async (configId) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/configs/${configId}/versions`);
    return response.data;
  } catch (error) {
    console.error('Failed to get config versions:', error);
    throw error;
  }
};

/**
 * Rollback configuration to previous version
 * @param {string} configId - Configuration ID
 * @param {number} targetVersion - Version to rollback to
 * @returns {Promise<Object>} Rolled back configuration
 */
export const rollbackConfig = async (configId, targetVersion) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/configs/${configId}/rollback`, {
      targetVersion,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to rollback config:', error);
    throw error;
  }
};

/**
 * Create PayrollSubmission from parsed employee data
 * @param {Object} data - Submission data
 * @param {string} data.companyId - AutomationCompany ID
 * @param {number} data.month - Month (1-12)
 * @param {number} data.year - Year
 * @param {string} data.projectCode - Project code (optional)
 * @param {Array} data.employees - Array of parsed employees
 * @param {string} data.configId - Parser configuration ID (optional)
 * @param {Object} data.emlMetadata - EML metadata (optional)
 * @returns {Promise<Object>} Created submission info
 */
export const createSubmission = async (data) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/create-submission`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create submission:', error);
    throw error;
  }
};

// ==================== FINGERPRINTING API ====================

/**
 * Suggest configuration based on uploaded file structure
 * @param {FormData} formData - Form data with EML or Excel file
 * @returns {Promise<Object>} Suggestions with fingerprint data
 */
export const suggestConfig = async (formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/suggest-config`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to get config suggestions:', error);
    throw error;
  }
};

/**
 * Generate/Update fingerprint for a configuration
 * @param {string} configId - Configuration ID
 * @param {FormData} formData - Form data with sample file
 * @returns {Promise<Object>} Updated config with fingerprint
 */
export const generateFingerprint = async (configId, formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/configs/${configId}/fingerprint`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to generate fingerprint:', error);
    throw error;
  }
};

/**
 * Preview fingerprint without saving
 * @param {FormData} formData - Form data with file
 * @returns {Promise<Object>} Fingerprint preview
 */
export const previewFingerprint = async (formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/fingerprint/preview`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to preview fingerprint:', error);
    throw error;
  }
};
